import { PartyTypes } from '../enums';
import { keepsBookedSlot } from './keeps-booked-slot';

export interface PartyExpectedEndInput {
  type?: PartyTypes;
  quoted_time: number;
  seated_time?: number | null;
  duration?: number | null;
}

/**
 * When a party is expected to release its table — the un-extended end of getPartyOccupancy's
 * interval (its `endBase`), before the overtime/completedAt adjustments that make occupancy a
 * live claim rather than a plan.
 *
 * - Reservations end at the *booked* end (quoted_time + duration): seating late doesn't push it
 *   out, seating early doesn't pull it in. One seated after its slot ended is a new seating
 *   (keepsBookedSlot) and ends like a walk-in.
 * - Walk-ins / waitlist / to-go end at seated_time + duration once seated — quoted_time is only
 *   their join/quote time. Before they're seated there is no seated anchor, so the booked
 *   window is used.
 *
 * Use this wherever the UI answers "when does this table free?" (floor-plan card "ends" copy,
 * overtime chip, serving-tab order, canvas table label) so those never disagree with the
 * conflict guard and the availability engine, which consume the same rule via getPartyOccupancy.
 * For drawing a party *bar* on a schedule view use getPartyDisplayWindow instead — a bar shows
 * where a party sits (an early seat slides it earlier), not when the table frees.
 *
 * `fallbackDuration` sizes parties with no recorded duration; it defaults to 0 so callers that
 * have no sensible default get end === the anchor rather than an invented window.
 */
export function getPartyExpectedEnd(party: PartyExpectedEndInput, fallbackDuration = 0): number {
  const duration = party.duration || fallbackDuration;
  const seated = party.seated_time ?? null;

  if (seated != null && !keepsBookedSlot(party, duration)) {
    return seated + duration;
  }

  return party.quoted_time + duration;
}
